import { Icon } from '@zoom/components';
import classnames from 'classnames';
import { differenceInMinutes, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { TFunction } from 'i18next';
import React from 'react';

import { useAuth } from '../../app/AuthorizationProvider';
import { getCalculatedHoursMinutes } from '../../utils/date';
import { EllipsisContent } from '../EllipsisContent';
import { ScheduleElementType } from './types';

interface BriefScheduleOverviewProps {
  convertToZonedDate?: boolean;
  date: number;
  elements: ScheduleElementType[];
  remote?: boolean;
  t: TFunction;
  timezone: string;
}

export const BriefScheduleOverview: React.FC<BriefScheduleOverviewProps> = ({
  convertToZonedDate = true,
  date,
  elements,
  remote = false,
  t,
  timezone,
}) => {
  const { localeObj } = useAuth();

  const zonedDate = convertToZonedDate ? utcToZonedTime(date, timezone) : new Date(date);

  const dayOfWeek = format(zonedDate, 'EEEE', { locale: localeObj });
  const fullDate = format(zonedDate, t('schedule.dateFormat'), { locale: localeObj });

  let totalScheduleDuration = 0;
  elements.forEach(({ custom: { startDateTime, endDateTime } }) => {
    totalScheduleDuration += differenceInMinutes(endDateTime, startDateTime);
  });

  const { hours: totalHours, minutes: totalMinutes } = getCalculatedHoursMinutes(totalScheduleDuration / 60);

  return (
    <div className="brief-overview">
      <div className="brief-overview__day">{dayOfWeek}</div>
      <div className="brief-overview__date">{fullDate}</div>
      {remote && (
        <div className="brief-overview__workplace" title={t('schedule.workingRemotely')}>
          <Icon kind="house" size={15} color="green" /> {t('schedule.workplaceType.remote')}
        </div>
      )}
      <table className="mx-auto">
        <tbody>
          {elements.map(({ color, custom: { startDateTime, endDateTime }, scheduleElementName, productive, key }) => (
            <tr className={classnames('brief-overview__element', { 'font-weight-bold': productive })} key={key}>
              <td className="brief-overview__element__time">{`${format(
                utcToZonedTime(startDateTime, timezone),
                t('date.formats.timeShort'),
                { locale: localeObj }
              )} - ${format(utcToZonedTime(endDateTime, timezone), t('date.formats.timeShort'), {
                locale: localeObj,
              })}`}</td>
              <td>
                <span className="brief-overview__element__square" style={{ background: color }} />
              </td>
              <EllipsisContent
                as="td"
                className="brief-overview__element__name"
                ellipsisProps={{ placement: 'bottom' }}
              >
                {scheduleElementName}
              </EllipsisContent>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="brief-overview__total">
        <span className="brief-overview__total-label">{t('time.total')}: </span>
        <strong>{totalHours}</strong>
        {` ${t('time.hrs')} `}
        <strong>{totalMinutes}</strong>
        {` ${t('time.min')}`}
      </div>
    </div>
  );
};
